import moment from 'moment'
import { formatTimes, formatHumanDate } from '../date-format-helpers'
import { HOURS_BANNER_DINELATE } from '../../constants'
import {
  TradingHour,
  Description,
  HourStatus,
  ParsedOpeningHourWithStatus,
} from './types'

const getDescription = (
  description: string,
  closed: boolean,
  isLateNight: boolean
): Description => ({
  get description() {
    if (description) {
      return description
    } else if (!description && !closed && isLateNight) {
      return HOURS_BANNER_DINELATE
    } else {
      return ''
    }
  },
  isColored: Boolean(description),
})

const groupOvernightAndMultiple = function groupOvernightAndMultiple(
  tradingHours: TradingHour[]
): TradingHour[][] {
  const result: TradingHour[][] = []
  let prevTradingHour: TradingHour | { hourStatus: null; date: string } = {
    hourStatus: null,
    date: '1900-01-01',
  }
  tradingHours.forEach(tradingHour => {
    const bothHaveStatus = (status: HourStatus): boolean =>
      [prevTradingHour, tradingHour].every(hour => hour.hourStatus === status)
    const isSameDate = moment(prevTradingHour.date).isSame(
      moment(tradingHour.date)
    )
    const isConsecutiveDate = moment(prevTradingHour.date)
      .add(1, 'day')
      .isSame(moment(tradingHour.date))
    const isMultipleGroup = bothHaveStatus(HourStatus.Multiple) && isSameDate
    const isOvernightGroup =
      bothHaveStatus(HourStatus.Overnight) && isConsecutiveDate
    if (isMultipleGroup || isOvernightGroup) {
      result[result.length - 1].push(tradingHour)
    } else {
      result.push([tradingHour])
    }
    prevTradingHour = tradingHour
  })
  return result
}

const extractHoursFromGroup = function extractHoursFromGroup(
  group: TradingHour[]
): string[] {
  const showTimes = (tradingHour: TradingHour): string =>
    // The hyphen character "-" is an en dash (alt + "-")
    `${tradingHour.openingTime} – ${tradingHour.closingTime}`
  const firstInGroup = group[0]
  if (firstInGroup.hourStatus === HourStatus.Closed) {
    return ['Closed']
  } else if (
    [HourStatus.Normal, HourStatus.Multiple].includes(firstInGroup.hourStatus)
  ) {
    return group.map(showTimes)
  } else if (firstInGroup.hourStatus === HourStatus.Overnight) {
    return [
      // The hyphen character "-" is an en dash (alt + "-")
      `${firstInGroup.openingTime} – ${group[1].closingTime} (${moment(
        group[1].date
      ).format('ddd')})`,
    ]
  } else {
    return ['']
  }
}

const parseGroupedHours = function parseGroupedHours(
  groupedTradingHours: TradingHour[][],
  centreTimeZone: string,
  isLateNight: boolean
): ParsedOpeningHourWithStatus[] {
  return groupedTradingHours.map(group => ({
    day: formatHumanDate(moment.tz(group[0].date, centreTimeZone), {
      timeZone: centreTimeZone,
      includeMonth: true,
    }),
    text: getDescription(group[0].description, group[0].closed, isLateNight),
    hours: extractHoursFromGroup(group),
    status: group[0].hourStatus,
  }))
}

export const parseOpeningHours = function parseOpeningHours(
  tradingHours: TradingHour[],
  centreTimeZone: string,
  centreFeatures?: string[]
): ParsedOpeningHourWithStatus[] {
  const isLateNight = (centreFeatures || []).includes('Late Night Dining')
  const formattedHours = formatTimes(tradingHours, centreTimeZone)
  const groupedHours = groupOvernightAndMultiple(formattedHours)
  // We may have partial overnight data
  const MIN_OVERNIGHT_GROUP_LENGTH = 2
  const filteredGroups = groupedHours.filter(
    group =>
      group[0].hourStatus !== HourStatus.Overnight ||
      group.length >= MIN_OVERNIGHT_GROUP_LENGTH
  )
  return parseGroupedHours(filteredGroups, centreTimeZone, isLateNight)
}
